import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from "formik"
import * as Yup from "yup"
import moment from 'moment'
import { useNavigate } from 'react-router-dom'

// components
import AppCard from '../../components/AppCard'
import AppForm from '../../components/form/form-project/AppForm'
import ActionButtonForm from 'src/components/form/general-input-form/ActionButtonForm'
import AppModalConfirmation from '../../components/modal/AppModalConfirmation'
import FormFileThumbnail from '../../components/form/general-input-form/FormFileThumbnail'
import AppTable from '../../components/AppTable'
import AppCardHeader from 'src/components/card/AppCardHeader'
import AppModalFilePreview from 'src/components/modal/AppModalFilePreview'

// helpers
import Axios from '../../helpers/axios'
import { formUrlQuery } from '../../helpers/format-queries'
import { singleClientsOptionsSelect, singleEmployeesOptionsSelect, singlePaymentTempoOptionSelect, singleCountriesOptionsSelect } from '../../helpers/format-options'
import { columnSupportingLinksList } from '../../components/table-column/TableColumnList'
import useToastMessage from 'src/helpers/toast-message'
import { timer } from "../../helpers/timer";
import {
  formikClientForOtherPage
} from '../../helpers/formik'

const AddProject = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { showError, showSuccess } = useToastMessage();

  const isLoading = useSelector((state) => state.isLoading)

  const [clientOptions, setClientOptions] = useState(false)
  const [countryOptions, setCountryOptions] = useState([])

  const [showConfirmAddContract, setShowConfirmAddContract] = useState({ show: false, item: {} })
  const [showConfirmAddOtherFiles, setShowConfirmAddOtherFiles] = useState({ show: false, item: {} })
  const [showModalAddSupportingLinks, setShowModalAddSupportingLinks] = useState({ show: false, item: {} })
  const [showModalConfirm, setShowModalConfirm] = useState({ show: false, type: '', text: '' })
  const [showModalCreateNewClient, setShowModalCreateNewClient] = useState({ show: false })

  const [showDeleteItem, setShowDeleteItem] = useState({ show: false, type: '', item: {}, index: 0 })
  const [showConfirmEdit, setShowConfirmEdit] = useState({ show: false, type: '', item: {}, index: 0 })
  const [showConfirmView, setShowConfirmView] = useState({ show: false, type: '', item: {}, index: 0 })

  const [headerTitle, setHeaderTitle] = useState('')

  const [projectSupportingLinkValue, setProjectSupportingLinkValue] = useState('')

  const [openFilePreview, setOpenFilePreview] = useState(false)

  const [employeeOptions, setEmployeeOptions] = useState([])
  const [employees, setEmployees] = useState(null)

  const [paymentTempoValue, setPaymentTempoValue] = useState(null)

  const [countryValue, setCountryValue] = useState(null)
  const [countryPhone, setCountryPhone] = useState(null)

  const [searchClientListFilter, setSearchClientListFilter] = useState("");

  const formik = useFormik({
    initialValues: {
      project_name: '',
      project_description: '',
      project_value: '',
      project_tax_value: '',
      project_start_date: '',
      project_end_date: '',
      client_value: null,
      project_contracts: [],
      project_supporting_files: [],
      project_supporting_docs: [],
      is_purchase_order: false,
      project_purchase_orders: [
        {
          order_number: '',
          order_value: '',
          order_created: '',
          order_notes: '',
          purchase_order_file: []
        }
      ],
      project_employees: [],
      project_terms: [
        {
          term_name: '',
          term_percentage: '',
          term_value: '',
          term_tax_value: ''
        }
      ],
      project_payment_tempo: '',
      project_other_payment_tempo: '',
      project_progress_status: null,
      terms_invoice: false,
      terms_bast: false,
      terms_po: false,
      terms_faktur_pajak: false,
      terms_pkp: false,
      terms_npwp: false,
      terms_progress_report: false,
      terms_additional_info: ''
    },
    validationSchema: Yup.object().shape({
      project_name: Yup.string()
        .required("Please enter project name."),
      project_description: Yup.string()
        .required("Please enter project description."),
      project_value: Yup.string()
        .required("Please enter project value."),
      project_tax_value: Yup.string()
        .required("Please enter project tax and other fees."),
      project_start_date: Yup.string()
        .required("Please select project start date."),
      project_end_date: Yup.string()
        .required("Please select project end date."),
      client_value: Yup.object().shape({
        label: Yup.string().required('Please select client name.')
      }).required('Please select client name.'),
      project_purchase_orders: Yup.array()
      .when('is_purchase_order', {
        is: true,
        then: (schema) => schema
          .of(
            Yup.object().shape({
              order_number: Yup.string().required("Please enter order number."),
              order_value: Yup.string().required("Please enter order value."),
              order_created: Yup.string().required("Please select order created on."),
              purchase_order_file: Yup.array().min(1, 'Please add at least one attachment')
            })
          ).min(1, 'Please add at least one purchase order.'),
        otherwise: (schema) => schema
      }),
      project_terms: Yup.array()
        .of(
          Yup.object().shape({
            term_name: Yup.string().required("Please enter term name."),
            term_percentage: Yup.string().required("Please enter term percentage."),
            term_value: Yup.string().required("Please enter term value."),
            term_tax_value: Yup.string().required("Please enter term tax value."),
          })
        ).min(1, 'Please add at least one project terms.'),
      project_payment_tempo: Yup.string()
        .required("Please select payment tempo."),
      project_other_payment_tempo: Yup.string()
        .when('project_payment_tempo', {
          is: 'Others',
          then: (schema) => schema.required("Please enter other payment tempo."),
          otherwise: (schema) => schema
        }),
      project_progress_status: Yup.string()
        .required("Please select project progress status."),
    }),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      handleSave(values)
      setSubmitting(false)
    },
  });

  const formikSupportingLink = useFormik({
    initialValues: {
      file_name: projectSupportingLinkValue?.file_name ? projectSupportingLinkValue.file_name : '',
      file_description: projectSupportingLinkValue?.file_description ? projectSupportingLinkValue.file_description : '',
      file_url: projectSupportingLinkValue?.file_url ? projectSupportingLinkValue.file_url : '',
    },
    validationSchema: Yup.object().shape({
      file_name: Yup.string()
        .required("Please enter file name."),
      file_description: Yup.string()
        .required("Please enter file description."),
      file_url: Yup.string()
        .required("Please enter file url."),
    }),
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      handleSaveProjectSupportingLink(values)
      resetForm()
      setSubmitting(false)
    },
  });

  const formikClient = formikClientForOtherPage({ handleSave: handleSaveNewClient })

  useEffect(() => {
    fetchAllClient()
    fetchAllEmployee()
    fetchAllCountries()
  }, [])

  useEffect(() => {
    let queries = {
      search: searchClientListFilter,
      limit: 10,
      offset: 0,
    };
    queries = formUrlQuery(queries);
    if (searchClientListFilter) {
      fetchAllClient(queries)
    } else {
      setClientOptions([])
    }
  }, [
    searchClientListFilter
  ])

  useEffect(() => {
    const firstErrorKey = Object.keys(formik.errors)[0];
    
    if (firstErrorKey) {
      const beforeFunction = () => {
        showError('There are fields that have not been filled in, please check again.')
        const errorElement = document.querySelector(`[id="${firstErrorKey}"]`);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          errorElement.focus();
        }
      };
      const afterFunction = () => {
        return  dispatch({
          type: 'SET_TOAST_MESSAGE',
          toastMessage: {
            isError: false,
            isShown: false,
            color: '',
            text: ''
          }
        })
      };
      timer(beforeFunction, afterFunction, 2000);
    }
  }, [formik.isSubmitting])

  const fetchAllClient = (queries) => {
    let url = `/api/tracker/v1/client`
    if (queries) {
      url += `?${queries}`
    }
    Axios({
      method: "get",
      url
    })
      .then(({ data }) => {
        if (data?.success) {
          setClientOptions(singleClientsOptionsSelect(data.value))
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  }

  const fetchAllEmployee = () => {
    Axios({
      method: "get",
      url: "/api/tracker/v1/employee/option/list",
    })
      .then(({ data }) => {
        setEmployeeOptions(singleEmployeesOptionsSelect(data?.value))
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  }

  const fetchAllCountries = () => {
    Axios({
      method: "get",
      url: "/api/tracker/v1/countries",
    })
      .then(({ data }) => {
        setCountryOptions(singleCountriesOptionsSelect(data))
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  };

  const handleOnDateChange = (date, name) => {
    formik.setValues((values) => ({
      ...values,
      [name]: date ? moment(date).format('YYYY-MM-DD HH:mm:ss') : ""
    }), false)
  }

  const handleInputValue = (values) => {
    const formData = new FormData()
    formData.append('name', values.project_name)
    formData.append('description', values.project_description)
    formData.append('value', parseFloat(values.project_value?.replace(/,/g, "")))
    formData.append('tax_value', parseFloat(values.project_tax_value?.replace(/,/g, "")))
    formData.append('start_date', values.project_start_date)
    formData.append('end_date', values.project_end_date)
    formData.append('client_id', values.client_value.value)
    formData.append('payment_tempo', values.project_payment_tempo)
    formData.append('other_payment_tempo', values.project_other_payment_tempo)
    formData.append('status', values.project_progress_status)
    formData.append('terms_invoice', values.terms_invoice)
    formData.append('terms_bast', values.terms_bast)
    formData.append('terms_po', values.terms_po)
    formData.append('terms_faktur_pajak', values.terms_faktur_pajak)
    formData.append('terms_pkp', values.terms_pkp)
    formData.append('terms_npwp', values.terms_npwp)
    formData.append('terms_progress_report', values.terms_progress_report)
    if (values?.terms_additional_info) {
      formData.append('terms_additional_info', values.terms_additional_info)
    }
    if (values?.project_contracts?.length > 0) {
      for (let i = 0; i < values.project_contracts.length; i++) {
        formData.append('contract', values.project_contracts[i])
      }
    }
    if (values?.project_supporting_files?.length > 0) {
      for (let i = 0; i < values.project_supporting_files.length; i++) {
        formData.append('supporting_file', values.project_supporting_files[i])
      }
    }
    if (values?.project_supporting_docs?.length > 0) {
      formData.append('supporting_links', JSON.stringify(values.project_supporting_docs))
    }
    if (values?.is_purchase_order == true && values?.project_purchase_orders?.length > 0) {
      for (let i = 0; i < values.project_purchase_orders.length; i++) {
        if (!values?.project_purchase_orders[i]?.purchase_order_file[0].file_url && values?.project_purchase_orders[i].purchase_order_file[0] instanceof File) {
          formData.append('purchase_order_attachment', values.project_purchase_orders[i].purchase_order_file[0])
        }
      }
      formData.append('purchase_orders', JSON.stringify(values.project_purchase_orders))
    }
    if (values?.project_employees?.length > 0) {
      formData.append('employees', JSON.stringify(values.project_employees))
    }
    if (values?.project_terms?.length > 0) {
      formData.append('terms', JSON.stringify(values.project_terms))
    }

    return formData
  }

  const handlePost = (payload) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "post",
      url: `/api/tracker/v1/project/create`,
      data: payload
    })
      .then(({ data }) => {
        if (data?.success) {
          showSuccess('New project has been successfully created')
          navigate('/project/list')
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const handleSave = (values) => {
    const inputPayload = handleInputValue(values)
    handlePost(inputPayload)
  }

  const handleSelectClient = (data) => {
    formik.setValues((values) => ({
      ...values,
      client_value: data || null
    }), false)
  }

  const handleRemoveFile = (index = 0, name) => {
    let key = name
    if (name === 'supporting-link') {
      key = 'project_supporting_docs'
    }

    if (name === 'purchase_order_file') {
      let purchaseOrder = formik.values.project_purchase_orders || []
      purchaseOrder[index].purchase_order_file = []
      
      formik.setValues((values) => ({
        ...values,
        project_purchase_orders: purchaseOrder
      }), false)
    } else {
      formik.setValues((values) => ({
        ...values,
        [key]: values[key]?.filter((_, i) => i !== index)
      }), false)
    }

    setShowDeleteItem({ show: false, type: '', item: {}, index: 0 })
  }

  const handleFileInput = (values, type) => {
    const files = values

    if (files) {
      if (type?.includes('project')) {
        formik.setValues((prevValues) => ({
          ...prevValues,
          [type]: [...prevValues[type], ...files]
        }), false)        
      }

      if (type === 'project_contracts') {
        setShowConfirmAddContract({ show: false, item: {} })
      }

      if (type === 'project_supporting_files') {
        setShowConfirmAddOtherFiles({ show: false, item: {} })
      }
    }
  }

  const handleUploadNewContracts = () => {
    setShowConfirmAddContract({ show: true, item: {} })
  }

  const handleUploadNewOtherFiles = () => {
    setShowConfirmAddOtherFiles({ show: true, item: {} })
  }

  const handleUploadNewSupportingLinks = () => {
    setShowModalAddSupportingLinks({ show: true, item: {} })
    setHeaderTitle('Add Project Supporting Links')
  }

  const handleSubmit = () => {
    if (showModalConfirm.type === 'project') {
      formik.handleSubmit()
    } else {
      formikSupportingLink.handleSubmit()
    }

    setShowModalConfirm({ show: false, type: '', text: '' })
  }

  const handleSaveProjectSupportingLink = (values) => {
    if (
      values.file_url !== '' &&
      values.file_name !== '' &&
      values.file_description !== ''
    ) {
      if (showConfirmEdit.show) {
        let supportingDocs = formik.values.project_supporting_docs
        supportingDocs[showConfirmEdit.index] = values

        formik.setValues((prevValues) => ({
          ...prevValues,
          'project_supporting_docs': supportingDocs
        }), false)

        setShowConfirmEdit({ show: false, type: '', item: {} })
      } else {
        formik.setValues((prevValues) => ({
          ...prevValues,
          'project_supporting_docs': [...prevValues['project_supporting_docs'], values]
        }), false)
      }

      setShowModalAddSupportingLinks({ show: false, item: {} })
    }
  }

  useEffect(() => {
    if (showConfirmEdit.show) {
      setShowModalAddSupportingLinks({ show: true, item: {} })
      setProjectSupportingLinkValue(showConfirmEdit.item)
      setHeaderTitle('Edit Project Supporting Links')
    }
  }, [showConfirmEdit])

  useEffect(() => {
    if (!showModalAddSupportingLinks.show) {
      setProjectSupportingLinkValue('')
    }
  }, [showModalAddSupportingLinks])

  const handleOnRowSuppLinkClick = (item, columnName) => {
    if (columnName !== 'action') {
      setShowConfirmView({ show: true, type: 'supporting-link', item })
    }
  }

  const handleAddPurchaseOrders = () => {
    formik.setValues((values) => ({
      ...values,
      project_purchase_orders: [
        ...values.project_purchase_orders,
        ...[
          {
            order_number: '',
            order_value: '',
            order_created: '',
            order_notes: '',
            purchase_order_file: []
          }
        ]
      ]
    }), false)
  }

  const handleChangePurchaseOrders = (e, informationIndex, type) => {
    let name = e?.target?.name
    if (!name) {
      if (!type) {
        name = 'order_created'
      } else {
        name = type
      }
    }
    let value = e?.target?.value
    if (!value) {
      value = e
    }

    formik.setValues((values) => {
      const newArrayInformation = values.project_purchase_orders.map((item, itemIndex) => {
        if (itemIndex == informationIndex) {
          return {
            ...item,
            [name]: name === 'order_created' ? moment(value).format('YYYY-MM-DD HH:mm:ss') : value
          };
        } else {
          return item;
        }
      });

      return {
        ...values,
        project_purchase_orders: [
          ...newArrayInformation,
        ]
      }
    }, false)
  }

  const handleRemovePurchaseOrders = (_, index) => {
    formik.setValues((values) => ({
      ...values,
      project_purchase_orders: values.project_purchase_orders.filter((_, itemIndex) => itemIndex !== index)
    }), false)
  }
  
  const handleSelectEmployee = (data) => {
    formik.setValues((values) => ({
      ...values,
      project_employees: data ? data : []
    }), false)

    setEmployees(data ? data : [])
  }

  const handleSelectPaymentTempo = (data) => {
    formik.setValues((values) => ({
      ...values,
      project_payment_tempo: data?.value
    }), false)

    setPaymentTempoValue(data ? data : null)
  }

  const handleAddProjectTerms = () => {
    formik.setValues((values) => ({
      ...values,
      project_terms: [
        ...values.project_terms,
        ...[
          {
            term_name: '',
            term_percentage: '',
            term_value: '',
            term_tax_value: ''
          }
        ]
      ]
    }), false)
  }

  const handleChangeProjectTerms = (e, informationIndex) => {
    let name = e?.target?.name
    let value = e?.target?.value

    formik.setValues((values) => {
      const newArrayInformation = values.project_terms.map((item, itemIndex) => {
        if (itemIndex == informationIndex) {
          return {
            ...item,
            [name]: value
          };
        } else {
          return item;
        }
      });

      return {
        ...values,
        project_terms: [
          ...newArrayInformation,
        ]
      }
    }, false)
  }

  const handleRemoveProjectTerms = (_, index) => {
    formik.setValues((values) => ({
      ...values,
      project_terms: values.project_terms.filter((_, itemIndex) => itemIndex !== index)
    }), false)
  }

  const handleSelectProgressStatus = (data) => {
    formik.setValues((values) => ({
      ...values,
      project_progress_status: data?.value
    }), false)
  }

  const handleSearchClient = (e) => {
    setSearchClientListFilter(e)
  }

  const handleCheckbox = (e) => {
    const isChecked = e.target.checked
    const name = e.target.name
    formik.setValues((values) => ({
      ...values,
      [name]: isChecked
    }), false)
  }

  const handleInputValueNewClient = (values) => {
    const formData = new FormData()

    formData.append('name', values.client_name)
    formData.append('description', values.client_description)
    formData.append('address', values.client_address)
    formData.append('pic_name', values.pic_name)
    formData.append('pic_contact_number', values.phone_number)
    formData.append('pic_country_id', values.pic_country_id)
    formData.append('pic_job_position', values.pic_job_position)
    formData.append('pic_email_address', values.pic_email_address)
    formData.append('pic_department', values.pic_department)
    formData.append('pic_additional_notes', values.pic_additional_notes)
    formData.append('country_id', values.client_country_id)

    return formData
  }

  const handlePostNewClient = (payload) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "post",
      url: `/api/tracker/v1/client/create`,
      data: payload
    })
      .then(({ data }) => {
        const { success } = data || {}
        if (success) {
          showSuccess('New client has been successfully created')
          formik.setValues((values) => ({
            ...values,
            client_value: {
              label: data?.value?.name,
              value: data?.value?.id
            }
          }), false)
          setShowModalCreateNewClient({ show: false })
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  function handleSaveNewClient(values) {
    const inputPayload = handleInputValueNewClient(values)
    handlePostNewClient(inputPayload)
  }

  const handleShowModalCreate = (name, value) => {
    if (name?.includes('client')) {
      setShowModalCreateNewClient({ show: true })
      formikClient.setValues((values) => ({
        ...values,
        client_name: value
      }), false)
    }
  }

  const handleSelectCountryPhone = (data) => {
    formikClient.setValues((values) => ({
      ...values,
      pic_country_id: data ? JSON.parse(data?.value).id : ''
    }), false)

    setCountryPhone(data? data?.value : null)
  }

  const handleSelectCountry = (data) => {
    if (data?.value) {
      formikClient.setValues((values) => ({
        ...values,
        client_country_id: data ? JSON.parse(data?.value).id : ''
      }), false)

      setCountryValue({
        label: data?.label,
        value: JSON.parse(data?.value)?.id
      })
    } else {
      setCountryValue(null)

      formikClient.setValues((values) => ({
        ...values,
        client_country_id: '',
      }), false)
    }
  }

  const handleSubmitNewClient = () => {
    formikClient.handleSubmit()

    setShowModalCreateNewClient({ show: false })
  }

  return (
    <>
      {openFilePreview && (
        <AppModalFilePreview
          setOpenFilePreview={setOpenFilePreview}
          file={openFilePreview}
        />
      )}
      {
        showConfirmAddContract.show && (
          <AppModalConfirmation 
            confirmationType='add-contract'
            itemName='project_contracts'
            itemDetails={showConfirmAddContract.item}
            showModal={showConfirmAddContract}
            setShowModal={setShowConfirmAddContract}
            errors={formik.errors?.project_contracts}
            handleChange={formik.handleChange}
            handleFileInput={handleFileInput}
            headerTitle={`Add Project Contracts`}
          />
        )
      }
      {
        showConfirmAddOtherFiles.show && (
          <AppModalConfirmation 
            confirmationType='add-other-files'
            itemName='project_supporting_files'
            itemDetails={showConfirmAddOtherFiles.item}
            showModal={showConfirmAddOtherFiles}
            setShowModal={setShowConfirmAddOtherFiles}
            errors={formik.errors?.project_supporting_files}
            handleChange={formik.handleChange}
            handleFileInput={handleFileInput}
            headerTitle={`Add Project Supporting Files`}
          />
        )
      }
      {
        showModalAddSupportingLinks.show && (
          <AppModalConfirmation 
            confirmationType='add-supporting-links'
            itemName='project_supporting_docs'
            itemDetails={showModalAddSupportingLinks.item}
            showModal={showModalAddSupportingLinks}
            setShowModal={setShowModalAddSupportingLinks}
            values={formikSupportingLink.values}
            errors={formikSupportingLink.errors}
            handleChange={formikSupportingLink.handleChange}
            headerTitle={headerTitle}
            handleSubmit={() => setShowModalConfirm({ show: true, type: 'supporting-link', text: showConfirmEdit.show ? 'Are you sure want to edit this project supporting links?' : 'Are you sure want to add a new project supporting links?' })}
          />
        )
      }
      {
        showConfirmView.show && showConfirmView?.type === 'supporting-link' && (
          <AppModalConfirmation 
            confirmationType='detail-supporting-link'
            itemName="detail-supporting-link"
            itemDetails={showConfirmView.item}
            itemType={showConfirmView.type}
            showModal={showConfirmView}
            setShowModal={setShowConfirmView}
          />
        )
      }
      {
        showDeleteItem.show && (
          <AppModalConfirmation 
            confirmationType='delete-item'
            itemName={`${showDeleteItem?.item?.file_original_name ? showDeleteItem?.item?.file_original_name : showDeleteItem?.item?.file_name ? showDeleteItem?.item?.file_name : showDeleteItem?.item?.name}`}
            itemDetails={showDeleteItem.item}
            itemType={showDeleteItem.type}
            itemIndex={showDeleteItem?.index}
            showModal={showDeleteItem}
            setShowModal={setShowDeleteItem}
            handleConfirmation={handleRemoveFile}
          />
        )
      }
      {
        showModalConfirm.show && (
          <AppModalConfirmation 
            confirmationType="confirmation"
            showModal={showModalConfirm}
            setShowModal={setShowModalConfirm}
            headerTitle="Confirmation"
            descriptionText={showModalConfirm.text}
            handleSubmit={handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
          />
        )
      }
      {
        showModalCreateNewClient.show && (
          <AppModalConfirmation 
            confirmationType='create-new-client'
            showModal={showModalCreateNewClient}
            setShowModal={setShowModalCreateNewClient}
            headerTitle="Create New Client"
            handleSubmit={handleSubmitNewClient}
            values={formikClient.values}
            errors={formikClient.errors}
            handleChange={formikClient.handleChange}
            countryOptions={countryOptions}
            countryValue={countryValue}
            countryPhone={countryPhone}
            handleSelectCountry={handleSelectCountry}
            handleSelectCountryPhone={handleSelectCountryPhone}
          />
        )
      }
      <AppCard 
        className='mb-4'
        headerTitle={<span className='fs-5 fs-md-4'>Add Project</span>}
        bodyContent={
          <AppForm 
            values={formik.values}
            handleChange={formik.handleChange}
            errors={formik.errors}
            onDateChange={handleOnDateChange}
            clientOptions={clientOptions}
            handleSelectClient={handleSelectClient}
            handleSearchInput={handleSearchClient}
            handleAddPurchaseOrders={handleAddPurchaseOrders}
            handleChangeInvoiceRecipients={handleChangePurchaseOrders}
            handleRemovePurchaseOrders={handleRemovePurchaseOrders}
            setOpenFilePreview={setOpenFilePreview}
            handleConfirmDelete={setShowDeleteItem}
            handleSelectEmployee={handleSelectEmployee}
            employees={employees}
            employeeOptions={employeeOptions}
            handleAddProjectTerms={handleAddProjectTerms}
            handleChangeProjectTerms={handleChangeProjectTerms}
            handleRemoveProjectTerms={handleRemoveProjectTerms}
            handleSelectPaymentTempo={handleSelectPaymentTempo}
            paymentTempoOptions={singlePaymentTempoOptionSelect()}
            paymentTempoValue={paymentTempoValue}
            handleSelectProgressStatus={handleSelectProgressStatus}
            handleCheckbox={handleCheckbox}
            handleShowModalCreate={handleShowModalCreate}
          />
        }
        lowerBodyContent={
          <>
            <AppCard
              className='mt-4 mb-4'
              headerTitle={
                <AppCardHeader
                  headerTitle={<span className='fs-6 fs-md-4'>Contracts</span>}
                  headerButton="Upload new"
                  handleCardHeaderButton={handleUploadNewContracts}
                />
              }
              bodyContent={
                <>
                  {
                    formik.values?.project_contracts?.length > 0 ? (
                      <FormFileThumbnail 
                        name='project_contracts'
                        colSize='3'
                        files={formik.values?.project_contracts}
                        className='justify-content-start align-items-center'
                        handleConfirmDelete={setShowDeleteItem}
                        handleViewFile={setOpenFilePreview}
                      />
                    )
                    : (
                      <>Not Found</>
                    )
                  }
                </>
              }
            />
            <AppCard
              className='mt-4 mb-4'
              headerTitle={
                <AppCardHeader
                  headerTitle={<span className='fs-6 fs-md-4'>Other supporting files</span>}
                  headerButton="Upload new"
                  handleCardHeaderButton={handleUploadNewOtherFiles}
                />
              }
              bodyContent={
                <>
                  {
                    formik.values?.project_supporting_files?.length > 0 ? (
                      <FormFileThumbnail 
                        name='project_supporting_files'
                        colSize='3'
                        files={formik.values?.project_supporting_files}
                        className='justify-content-start align-items-center'
                        handleConfirmDelete={setShowDeleteItem}
                        handleViewFile={setOpenFilePreview}
                      />
                    )
                    : (
                      <>Not Found</>
                    )
                  }
                </>
              }
            />
            <AppCard
              className='mt-4 mb-4'
              headerTitle={
                <AppCardHeader
                  headerTitle={<span className='fs-6 fs-md-4'>Supporting links</span>}
                  headerButton="Create new"
                  handleCardHeaderButton={handleUploadNewSupportingLinks}
                />
              }
              bodyContent={
                <AppTable
                  supportingLinksListPage
                  tableColumns={columnSupportingLinksList}
                  tableContent={formik.values.project_supporting_docs}
                  isShowPagination={false}
                  isShowModalEdit={true}
                  setShowConfirmEdit={setShowConfirmEdit}
                  setShowConfirmDelete={setShowDeleteItem}
                  handleOnRowClick={handleOnRowSuppLinkClick}
                  isClickable={true}
                />
              }
            />
          </>
        }
        footerContent={
          <ActionButtonForm
            isLoading={isLoading}
            disabled={formik.isSubmitting}
            onCancel={() => navigate('/project/list')}
            onSave={() => setShowModalConfirm({ show: true, type: 'project', text: 'Are you sure want create a new project?' })}
          />
        }
      />
    </>
  )
}

export default AddProject